'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, BadgeAlert, TrendingDown, MapPin, ChevronRight, RefreshCw, X } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface CertExpiryWarning {
  id: string;
  crewName: string;
  licenseType: string;
  daysUntilExpiry: number;
  expiryDate: string;
  severity: 'critical' | 'warning';
}

interface LowGradeWarning {
  id: string;
  crewName: string;
  crewCode: string;
  role: string;
  grade: string;
  severity: 'critical' | 'warning';
}

interface UnderstaffedRoute {
  id: string;
  routeName: string;
  routeCode: string;
  dutyCode: string;
  missingRoles: string[];
  dutyDate: string;
  severity: 'critical' | 'warning';
}

interface HighRiskData {
  certExpiry: CertExpiryWarning[];
  lowGrades: LowGradeWarning[];
  understaffed: UnderstaffedRoute[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CERT_EXPIRY_CRITICAL_DAYS = 7;
const CERT_EXPIRY_WARNING_DAYS = 30;

function formatDaysLabel(days: number): string {
  if (days <= 0) return 'Expired';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

// ─── Sub-cards ────────────────────────────────────────────────────────────────

function SectionCard({
  icon: Icon,
  title,
  count,
  colorClass,
  bgClass,
  borderClass,
  children,
  actionHref,
  actionLabel,
}: {
  icon: React.ElementType;
  title: string;
  count: number;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  children: React.ReactNode;
  actionHref: string;
  actionLabel: string;
}) {
  if (count === 0) return null;

  return (
    <div className={`glass-card rounded-2xl border ${borderClass} p-4 flex flex-col gap-3`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${bgClass}`}>
            <Icon size={15} className={colorClass} />
          </div>
          <div>
            <p className="text-sm font-700 text-foreground leading-tight">{title}</p>
            <p className={`text-[10px] font-700 uppercase tracking-wider ${colorClass}`}>
              {count} {count === 1 ? 'item' : 'items'} flagged
            </p>
          </div>
        </div>
        <Link
          href={actionHref}
          className={`flex items-center gap-0.5 text-xs font-700 ${colorClass} hover:opacity-80 transition-opacity`}
        >
          {actionLabel} <ChevronRight size={11} />
        </Link>
      </div>

      {/* Items */}
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function WarningRow({
  label,
  detail,
  badge,
  badgeClass,
}: {
  label: string;
  detail: string;
  badge: string;
  badgeClass: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-t border-[var(--glass-border)] first:border-t-0">
      <div className="min-w-0">
        <p className="text-xs font-600 text-foreground truncate">{label}</p>
        <p className="text-[11px] text-muted-foreground leading-snug">{detail}</p>
      </div>
      <span className={`shrink-0 text-[9px] font-700 uppercase tracking-widest px-2 py-0.5 rounded-md ${badgeClass}`}>
        {badge}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HighRiskWarningCards() {
  const [data, setData] = useState<HighRiskData>({ certExpiry: [], lowGrades: [], understaffed: [] });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [lastRefreshedLabel, setLastRefreshedLabel] = useState<string>('');

  const fetchData = useCallback(async () => {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    const warningCutoff = new Date();
    warningCutoff.setDate(warningCutoff.getDate() + CERT_EXPIRY_WARNING_DAYS);

    try {
      // 1. Cert / License Expiry — licenses expiring within 30 days
      const { data: licenseRows } = await supabase
        .from('licenses')
        .select('id, crew_name, license_type, expiry_date, days_until_expiry, status')
        .lte('expiry_date', warningCutoff.toISOString().split('T')[0])
        .neq('status', 'Expired')
        .order('expiry_date', { ascending: true })
        .limit(10);

      const certExpiry: CertExpiryWarning[] = (licenseRows ?? []).map((r) => {
        const days = r.days_until_expiry ?? 0;
        return {
          id: r.id,
          crewName: r.crew_name,
          licenseType: r.license_type,
          daysUntilExpiry: days,
          expiryDate: r.expiry_date,
          severity: days <= CERT_EXPIRY_CRITICAL_DAYS ? 'critical' : 'warning',
        };
      });

      // 2. Repeated Low Performance Grades — crew with grade C, D, or F
      const { data: gradeRows } = await supabase
        .from('crew_members')
        .select('id, name, crew_code, role, grade')
        .in('grade', ['C', 'D', 'F'])
        .eq('status', 'Available')
        .order('grade', { ascending: true })
        .limit(10);

      const lowGrades: LowGradeWarning[] = (gradeRows ?? []).map((r) => ({
        id: r.id,
        crewName: r.name,
        crewCode: r.crew_code,
        role: r.role,
        grade: r.grade,
        severity: r.grade === 'D' || r.grade === 'F' ? 'critical' : 'warning',
      }));

      // 3. Understaffed Routes — today's duties missing driver or conductor
      const { data: dutyRows } = await supabase
        .from('duty_assignments')
        .select('id, duty_code, route, route_name, driver1_name, conductor_name, duty_date, status')
        .eq('duty_date', today)
        .in('status', ['Scheduled', 'Active'])
        .limit(20);

      const understaffed: UnderstaffedRoute[] = (dutyRows ?? [])
        .filter((d) => {
          const missingDriver = !d.driver1_name || d.driver1_name === '—' || d.driver1_name === '';
          const missingConductor = !d.conductor_name || d.conductor_name === '—' || d.conductor_name === '';
          return missingDriver || missingConductor;
        })
        .map((d) => {
          const missingRoles: string[] = [];
          if (!d.driver1_name || d.driver1_name === '—' || d.driver1_name === '') missingRoles.push('Driver');
          if (!d.conductor_name || d.conductor_name === '—' || d.conductor_name === '') missingRoles.push('Conductor');
          return {
            id: d.id,
            routeName: d.route_name || d.route || 'Unknown Route',
            routeCode: d.route || '',
            dutyCode: d.duty_code,
            missingRoles,
            dutyDate: d.duty_date,
            severity: missingRoles.length >= 2 ? 'critical' : 'warning',
          };
        })
        .slice(0, 8);

      setData({ certExpiry, lowGrades, understaffed });
      const now = new Date();
      setLastRefreshed(now);
      setLastRefreshedLabel(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }));
    } catch (err) {
      console.error('HighRiskWarningCards: fetch error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [fetchData]);

  const totalWarnings = data.certExpiry.length + data.lowGrades.length + data.understaffed.length;
  const hasCritical =
    data.certExpiry.some((w) => w.severity === 'critical') ||
    data.lowGrades.some((w) => w.severity === 'critical') ||
    data.understaffed.some((w) => w.severity === 'critical');

  if (loading) {
    return (
      <div className="mb-6 glass-card rounded-2xl p-5 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-1/4" />
            <div className="h-2.5 bg-muted rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (totalWarnings === 0 || dismissed) return null;

  return (
    <div className="mb-6">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {hasCritical && (
            <span className="w-2 h-2 rounded-full bg-negative animate-pulse" />
          )}
          <ShieldAlert
            size={15}
            className={hasCritical ? 'text-negative' : 'text-warning'}
          />
          <h3 className="text-sm font-700 text-foreground">High-Risk Duty Warnings</h3>
          <span
            className={`text-[10px] font-700 uppercase tracking-wider px-2 py-0.5 rounded-full ${
              hasCritical ? 'bg-negative/12 text-negative' : 'bg-warning/12 text-warning'
            }`}
          >
            {totalWarnings} active
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-[10px] text-muted-foreground tabular-nums hidden sm:block">
              Updated {lastRefreshedLabel}
            </span>
          )}
          <button
            onClick={fetchData}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-black/8 dark:hover:bg-white/8 transition-colors"
            title="Refresh warnings"
          >
            <RefreshCw size={11} className="text-muted-foreground" />
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-black/8 dark:hover:bg-white/8 transition-colors"
            title="Dismiss all"
          >
            <X size={11} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Warning Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Card 1: Cert / License Expiry */}
        <SectionCard
          icon={BadgeAlert}
          title="Crews Near Cert Expiry"
          count={data.certExpiry.length}
          colorClass={data.certExpiry.some((w) => w.severity === 'critical') ? 'text-negative' : 'text-warning'}
          bgClass={data.certExpiry.some((w) => w.severity === 'critical') ? 'bg-negative/12' : 'bg-warning/12'}
          borderClass={data.certExpiry.some((w) => w.severity === 'critical') ? 'border-negative/25' : 'border-warning/25'}
          actionHref="/license-management"
          actionLabel="View Licenses"
        >
          {data.certExpiry.slice(0, 4).map((w) => (
            <WarningRow
              key={w.id}
              label={w.crewName}
              detail={`${w.licenseType} — expires ${w.expiryDate}`}
              badge={formatDaysLabel(w.daysUntilExpiry)}
              badgeClass={
                w.severity === 'critical' ?'bg-negative/12 text-negative' :'bg-warning/12 text-warning'
              }
            />
          ))}
          {data.certExpiry.length > 4 && (
            <p className="text-[11px] text-muted-foreground pt-1">
              +{data.certExpiry.length - 4} more — <Link href="/license-management" className="text-primary hover:underline">view all</Link>
            </p>
          )}
        </SectionCard>

        {/* Card 2: Repeated Low Performance Grades */}
        <SectionCard
          icon={TrendingDown}
          title="Repeated Low Performance"
          count={data.lowGrades.length}
          colorClass={data.lowGrades.some((w) => w.severity === 'critical') ? 'text-negative' : 'text-warning'}
          bgClass={data.lowGrades.some((w) => w.severity === 'critical') ? 'bg-negative/12' : 'bg-warning/12'}
          borderClass={data.lowGrades.some((w) => w.severity === 'critical') ? 'border-negative/25' : 'border-warning/25'}
          actionHref="/duty-history-performance"
          actionLabel="View Performance"
        >
          {data.lowGrades.slice(0, 4).map((w) => (
            <WarningRow
              key={w.id}
              label={`${w.crewName} (${w.crewCode})`}
              detail={`${w.role} — grade ${w.grade} below threshold`}
              badge={`Grade ${w.grade}`}
              badgeClass={
                w.severity === 'critical' ?'bg-negative/12 text-negative' :'bg-warning/12 text-warning'
              }
            />
          ))}
          {data.lowGrades.length > 4 && (
            <p className="text-[11px] text-muted-foreground pt-1">
              +{data.lowGrades.length - 4} more — <Link href="/duty-history-performance" className="text-primary hover:underline">view all</Link>
            </p>
          )}
        </SectionCard>

        {/* Card 3: Understaffed Routes */}
        <SectionCard
          icon={MapPin}
          title="Understaffed Routes Today"
          count={data.understaffed.length}
          colorClass={data.understaffed.some((w) => w.severity === 'critical') ? 'text-negative' : 'text-warning'}
          bgClass={data.understaffed.some((w) => w.severity === 'critical') ? 'bg-negative/12' : 'bg-warning/12'}
          borderClass={data.understaffed.some((w) => w.severity === 'critical') ? 'border-negative/25' : 'border-warning/25'}
          actionHref="/duty-assignment"
          actionLabel="Assign Crew"
        >
          {data.understaffed.slice(0, 4).map((w) => (
            <WarningRow
              key={w.id}
              label={`${w.routeName} — ${w.dutyCode}`}
              detail={`Missing: ${w.missingRoles.join(', ')}`}
              badge={w.severity === 'critical' ? 'Unassigned' : 'Partial'}
              badgeClass={
                w.severity === 'critical' ?'bg-negative/12 text-negative' :'bg-warning/12 text-warning'
              }
            />
          ))}
          {data.understaffed.length > 4 && (
            <p className="text-[11px] text-muted-foreground pt-1">
              +{data.understaffed.length - 4} more — <Link href="/duty-assignment" className="text-primary hover:underline">assign now</Link>
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
